import React, { useState } from 'react';
import { formatDate } from '@/utils';
import { Button } from '@/components/ui/Button';
import { ResponseVersionDetails } from '@/hooks/useResponses';

interface ResponseVersionHistoryProps {
  versions: ResponseVersionDetails[];
  currentVersion: number;
  onViewVersion: (versionNumber: number) => void;
  onCompareVersions?: (v1: number, v2: number) => void;
  isLoading?: boolean;
}

export const ResponseVersionHistory: React.FC<ResponseVersionHistoryProps> = ({
  versions,
  currentVersion,
  onViewVersion,
  onCompareVersions,
  isLoading = false
}) => {
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);

  const toggleVersionSelection = (versionNumber: number) => {
    if (selectedVersions.includes(versionNumber)) {
      setSelectedVersions(selectedVersions.filter((v) => v !== versionNumber));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, versionNumber]);
    } else {
      // Replace the first selected version with the new one
      setSelectedVersions([selectedVersions[1], versionNumber]);
    }
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2 && onCompareVersions) {
      const [v1, v2] = selectedVersions.sort((a, b) => a - b);
      onCompareVersions(v1, v2);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Version History
        </h3>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
          <p className="mt-2">Loading versions...</p>
        </div>
      </div>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Version History
        </h3>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No version history available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Version History ({versions.length})
          </h3>

          {onCompareVersions && selectedVersions.length === 2 && (
            <Button onClick={handleCompare} size="sm" variant="outline">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                />
              </svg>
              Compare Selected
            </Button>
          )}
        </div>

        {onCompareVersions && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {selectedVersions.length === 0
              ? 'Select up to 2 versions to compare'
              : selectedVersions.length === 1
              ? 'Select one more version to compare'
              : 'Click "Compare Selected" to view differences'}
          </p>
        )}
      </div>

      {/* Versions List */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
        {versions.map((version) => {
          const isCurrent = version.versionNumber === currentVersion;
          const isSelected = selectedVersions.includes(version.versionNumber);

          return (
            <div
              key={version.versionNumber}
              className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                isCurrent ? 'bg-blue-50 dark:bg-blue-900/20' : ''
              } ${isSelected ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Version Header */}
                  <div className="flex items-center space-x-2 mb-1">
                    {onCompareVersions && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleVersionSelection(version.versionNumber)}
                        disabled={
                          !isSelected && selectedVersions.length >= 2
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    )}

                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Version {version.versionNumber}
                    </h4>

                    {isCurrent && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        Current
                      </span>
                    )}
                  </div>

                  {/* Version Info */}
                  <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    {version.createdBy && (
                      <p>
                        Created by{' '}
                        <span className="font-medium">
                          {version.createdBy.firstName} {version.createdBy.lastName}
                        </span>
                      </p>
                    )}
                    <p>{formatDate(version.createdAt)}</p>

                    {version.comment && (
                      <p className="italic text-gray-500 dark:text-gray-400 mt-1">
                        "{version.comment}"
                      </p>
                    )}
                  </div>

                  {/* Version Content Preview */}
                  {version.content && (
                    <div className="mt-2 text-xs">
                      {version.content.subject && (
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded px-2 py-1 mb-1">
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Subject:
                          </span>{' '}
                          <span className="text-gray-600 dark:text-gray-400">
                            {version.content.subject.length > 50
                              ? `${version.content.subject.substring(0, 50)}...`
                              : version.content.subject}
                          </span>
                        </div>
                      )}

                      {version.content.body && (
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded px-2 py-1">
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Body:
                          </span>{' '}
                          <span className="text-gray-600 dark:text-gray-400">
                            {version.content.body.length > 100
                              ? `${version.content.body.substring(0, 100)}...`
                              : version.content.body}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attachments Count */}
                  {version.attachments && version.attachments.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <svg
                        className="inline w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {version.attachments.length} attachment
                      {version.attachments.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* View Button */}
                <Button
                  onClick={() => onViewVersion(version.versionNumber)}
                  variant="ghost"
                  size="sm"
                  className="ml-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
